/**
 * @file: TaskManager.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { ProductManifestManager } from '@euler/app/flows/task/functions/task-detail/product-manifest/ProductManifestManager';
import { isDebugEnabled } from '@euler/functions/debug';
import {
  Inventory,
  InventoryQuery,
  useInventoryLookup,
} from '@euler/functions/useInventory';
import {
  deleteCachedFile,
  readCachedFileContents,
  writeContentsToCachedFile,
} from '@euler/lib/cache/cacheUtil';
import { sentry } from '@euler/lib/integration/sentry';
import { AuthenticatedUserInfo, UpdateTaskInformation } from '@euler/model';
import { FinishTaskRequestPayload } from '@euler/model/FinishTaskRequestPayload';
import {
  CommonTaskStatus,
  DeliveryCheckSceneType,
  InspectionCategory,
  InspectionTaskStatus,
  InspectionTemplateSceneType,
  ResourceAccessScope,
  SamplingCheckResultType,
} from '@euler/model/enum';
import {
  CachedTaskDetail,
  Inspection,
  TaskDetail,
  TaskDetailProjection,
  TaskDetailVersions,
  TaskExtraInfo,
  VersionedResponse,
} from '@euler/model/task-detail';
import { TaskService } from '@euler/services';
import { ServiceFactory } from '@euler/services/factory';
import { joinPath, makeDebug } from '@euler/utils';
import { dateFromValue } from '@euler/utils/datetime';
import { freeze, produce } from 'immer';
import { WritableDraft } from 'immer/dist/internal';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';
import { MediaFileUploadController } from './MediaFileUploadController';
import { ConstructionManager } from './construction/ConstructionManager';
import { DeliveryCheckManager } from './delivery-check/DeliveryCheckManager';
import { SamplingCheckManager } from './delivery-check/SamplingCheckManager';
import { InspectionManager } from './inspection/InspectionManager';
import { PreInspectionManager } from './inspection/PreInspectionManager';
import { MaintenancePlanManager } from './maintenance-plan/MaintenancePlanManager';
import { QuotationManager } from './quotation/QuotationManager';
import { ITaskManager, TaskFileUploadRequestContext } from './types';
import { detailVersionsMatch, getDetailVersions } from './versions';

const debug = makeDebug('taskmanager', isDebugEnabled('taskManager'));

const kDetailProps: (keyof TaskDetailProjection)[] = [
  'basicInfo',
  'preInspection',
  'inspection',
  'obdInspection',
  'construction',
  'deliveryCheck',
  'samplingCheck',
  'maintenancePlan',
  'productManifest',
  'quotation',
];

type TaskManagerOptions = {
  mediaFileUpload?: {
    concurrency?: number;
  };
  largeMediaFileUpload?: {
    concurrency?: number;
  };
};

export type TaskDetailState =
  | { status: 'idle' }
  | {
      status: 'loading';
      refreshing?: boolean;
    }
  | {
      status: 'loaded';
    }
  | {
      status: 'error';
      error?: Error;
    };

/**
 * @class Represents a vehicle service task manager implementation.
 */
export class TaskManager implements ITaskManager {
  // the version of the task manager's protocol implementation.
  static readonly VERSION = '1.3';

  private static readonly _taskManagerMap = new Map<string, TaskManager>();

  // the taskNo of the managed task
  readonly taskNo: string;

  readonly taskService: TaskService;

  readonly serviceFactory: ServiceFactory;

  // pre-inspection manager.
  readonly preInspectionManager = new PreInspectionManager(this);

  // inspection manager.
  readonly inspectionManager = new InspectionManager<Inspection>(
    InspectionCategory.Normal,
    this,
  );

  // construction manager.
  readonly constructionManager = new ConstructionManager(this);

  // delivery check manager
  readonly deliveryCheckManager = new DeliveryCheckManager(this);

  // sampling check manager
  readonly samplingCheckManager = new SamplingCheckManager(this);

  // maintenance plan manager
  readonly maintenancePlanManager = new MaintenancePlanManager(this);

  // quotation manager
  readonly quotationManager: QuotationManager;

  // product manifest manager
  readonly productManifestManager: ProductManifestManager;

  // the shared file upload controller per task.
  readonly uploadController: MediaFileUploadController<TaskFileUploadRequestContext>;

  // the shared file upload controller per task.
  readonly largeMediaUploadController: MediaFileUploadController<TaskFileUploadRequestContext>;

  // observable that delivers the most recent version of task detail.
  readonly detail$ = new Subject<CachedTaskDetail>();
  readonly state$ = new BehaviorSubject<TaskDetailState>({ status: 'idle' });

  // the task extra information.
  readonly extraInfo$ = new BehaviorSubject<TaskExtraInfo | undefined>(
    undefined,
  );

  // the key of the cached detail data in local cache file system
  private readonly cacheFilePath: string;

  private _active = false;
  private _internalSubscription?: Subscription;
  private _detail: CachedTaskDetail | undefined;
  private _inventoryQuery!: InventoryQuery;

  private constructor(
    public currentUser: AuthenticatedUserInfo,
    taskNo: string,
    serviceFactory: ServiceFactory,
    options?: TaskManagerOptions,
  ) {
    this.taskNo = taskNo;
    this.cacheFilePath = this.getCacheFilePath('detail.json');
    this.serviceFactory = serviceFactory;
    this.taskService = serviceFactory.taskService;
    this.uploadController = new MediaFileUploadController({
      mediaFileService: serviceFactory.mediaFileService,
      concurrency: options?.mediaFileUpload?.concurrency,
    });
    this.largeMediaUploadController = new MediaFileUploadController({
      mediaFileService: serviceFactory.mediaFileService,
      concurrency: options?.largeMediaFileUpload?.concurrency ?? 3,
    });
    this._detail = undefined;
    this.productManifestManager = new ProductManifestManager(this);
    this.quotationManager = new QuotationManager(this);
  }

  get inventoryQuery() {
    return this._inventoryQuery;
  }

  /**
   * Get the observable that emits a sequence of task detail objects.
   */
  get observable(): Observable<CachedTaskDetail> {
    void this.begin();
    return this.detail$;
  }

  /**
   * Gets the cached detail.
   */
  get cachedDetail(): CachedTaskDetail {
    if (!this._detail) {
      throw new Error('cached detail does not exist, this must be bug');
    }
    return this._detail!;
  }

  get cachedVersions(): TaskDetailVersions | undefined {
    if (!this._detail) return undefined;
    return getDetailVersions(this._detail);
  }

  /**
   * Get the task manager instance for the given task.
   */
  static get(
    currentUser: AuthenticatedUserInfo,
    taskNo: string,
    serviceFactory: ServiceFactory,
    options?: TaskManagerOptions,
  ) {
    let manager = this._taskManagerMap.get(taskNo);
    if (!manager) {
      manager = new TaskManager(currentUser, taskNo, serviceFactory, options);
      this._taskManagerMap.set(taskNo, manager);
    }
    return manager;
  }

  static tryRecover(taskNo: string) {
    this._taskManagerMap.delete(taskNo);
  }

  useInventoryQuery(inventory: Inventory) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    this._inventoryQuery = useInventoryLookup(inventory);
    return this._inventoryQuery;
  }

  getCacheFilePath(path: string) {
    return joinPath(`task/${this.taskNo}/cache`, path);
  }

  async begin() {
    if (this._active) return;

    this._setupCacheAutoFlush();

    void this._load();

    this._active = true;

    debug('[%s] started', this.taskNo);
  }

  async getFinishPreparedData() {
    return await this.taskService.getFinishTaskPreparedData(this.taskNo, true);
  }

  async finish(payload: FinishTaskRequestPayload) {
    const resp = await this.taskService.finishTask(this.taskNo, payload);
    this.produceWithVersions(resp.versions, draft => {
      draft.deliveryCheck.status = CommonTaskStatus.Finished;
      draft.deliveryCheck.finishedAt = dateFromValue(resp.timestamp);
      draft.deliveryCheck.finishedBy = this.currentUser.user.name;
      draft.basicInfo.status = InspectionTaskStatus.Finished;
      draft.basicInfo.finishedAt = dateFromValue(resp.timestamp);
    });
  }

  async completeDeliveryChecksAndFinishTask() {
    const resp = await this.taskService.completeDeliveryChecksAndFinishTask(
      this.taskNo,
    );
    this.produceWithVersions(resp.versions, draft => {
      draft.deliveryCheck.status = CommonTaskStatus.Finished;
      draft.deliveryCheck.finishedAt = dateFromValue(resp.timestamp);
      draft.deliveryCheck.finishedBy = this.currentUser.user.name;
      draft.basicInfo.status = InspectionTaskStatus.Finished;
      draft.basicInfo.finishedAt = dateFromValue(resp.timestamp);
    });
  }

  end() {
    this.preInspectionManager.dispose();
    this.inspectionManager.dispose();
    this.constructionManager.dispose();
    this.deliveryCheckManager.dispose();
    this.productManifestManager.dispose();

    this._internalSubscription?.unsubscribe();
    this._internalSubscription = undefined;
    this._active = false;
    this._detail = undefined;

    TaskManager._taskManagerMap.delete(this.taskNo);

    this.detail$.complete();
    this.state$.complete();
    this.extraInfo$.complete();

    debug('[%s] stopped', this.taskNo);
  }

  async refresh(silence?: boolean) {
    await this._fetch({ force: true, refreshing: !silence });
    await this.fetchTaskExtraInfo();
  }

  async fetchTaskExtraInfo() {
    try {
      debug('fetching task extra info... ');
      const cacheFile = this.getCacheFilePath('extra-info.json');
      const contents = await readCachedFileContents(cacheFile);
      if (contents) {
        try {
          const cached = JSON.parse(contents);
          this.extraInfo$.next(cached);
        } catch (e) {
          console.error('error parse cached task extra info: ', e);
        }
      }
      const extraInfo = await this.taskService.getTaskExtraInfo(this.taskNo, {
        issuesFilter: { scope: ResourceAccessScope.Org },
        serviceRecordsFilter: { scope: ResourceAccessScope.Org },
      });
      this.extraInfo$.next(extraInfo);
      try {
        await writeContentsToCachedFile(cacheFile, JSON.stringify(extraInfo));
      } catch (e) {
        console.error('error save task extra info to cache: ', e);
      }
    } catch (e) {
      debug('error fetch task extra info', e);
      sentry.captureException(e);
    }
  }

  async updateBasicInfo(params: UpdateTaskInformation) {
    const { versions } = await this.taskService.updateTaskInformation(
      this.taskNo,
      params,
    );
    this.produceWithVersions(versions, (draft, original) => {
      const { dashboardImgUrl, deliveryCheckSignatureImgUrl, ...rest } = params;
      const { basicInfo } = draft;

      basicInfo.version = versions.basicInfo;

      const keys = Object.keys(rest);
      for (const p of keys) {
        const value = (rest as any)[p] as any;
        if (p === 'mileage') {
          basicInfo.vehicleMileage = value;
        } else if (p === 'serviceType') {
          basicInfo.orderType = value;
        } else {
          (basicInfo as any)[p] = value;
        }
      }

      if (dashboardImgUrl || deliveryCheckSignatureImgUrl) {
        if (dashboardImgUrl) {
          draft.basicInfo.dashboardImageUrl = dashboardImgUrl;
          draft.preInspection.dashboardImgUrl = dashboardImgUrl;
          if (draft.preInspection.status === CommonTaskStatus.Pending) {
            draft.preInspection.status = CommonTaskStatus.InProgress;
          }
          const index = original.preInspection.flows.findIndex(
            x => x.template.sceneType === InspectionTemplateSceneType.Dashboard,
          );
          if (
            index >= 0 &&
            original.preInspection.flows[index].status === 'pending'
          ) {
            draft.preInspection.flows[index].status = 'inprogress';
          }
        }
        if (deliveryCheckSignatureImgUrl) {
          draft.preInspection.signatureImgUrl = deliveryCheckSignatureImgUrl;
        }
        draft.preInspection.version = versions.preInspection;
      }
    });
  }

  produceWithVersions(
    versions: TaskDetailVersions,
    recipe: (
      draft: WritableDraft<CachedTaskDetail>,
      original: CachedTaskDetail,
    ) => void,
  ) {
    const projection: TaskDetailProjection = {};
    const current =
      this._detail ??
      ({ __VERSION: TaskManager.VERSION } as any as CachedTaskDetail);

    if (!this._active) return current;

    const detail = produce(current, draft => {
      recipe(draft, current);
      draft.version = versions.detail;
      draft.__TIMESTAMP = Date.now();

      for (const key of kDetailProps) {
        if (draft[key] == null || draft[key].version !== versions[key]) {
          projection[key] = true;
        }
      }
    });

    this.detail$.next(freeze(detail));

    // schedule fetch data from server.
    if (Object.keys(projection).length) {
      void this._fetch({ projection });
    }

    return detail;
  }

  /**
   * Sync task detail information w/ server.
   * @param options
   */
  async sync(options?: {
    refreshing?: boolean;
    callback?: (
      error: unknown | undefined,
      resp: VersionedResponse<TaskDetail> | undefined,
    ) => void;
  }) {
    if (!this._active) return;
    await this._fetch(options);
    await this.fetchTaskExtraInfo();
  }

  //#region private implementation details

  private async _load() {
    // start initial loading of the task detail data.
    let cachedVersions: TaskDetailVersions | undefined = undefined;

    // notify that the detail is being loaded or refreshed...
    this.state$.next({ status: 'loading' });

    const contents = await readCachedFileContents(this.cacheFilePath);
    if (contents != null) {
      try {
        const cached: CachedTaskDetail = JSON.parse(contents);

        // initialize all properties below which are added afterwards
        if (cached.maintenancePlan == null) {
          cached.maintenancePlan = { plan: undefined, version: 0 };
        }

        if (cached.productManifest == null) {
          cached.productManifest = {
            items: [],
            version: 0,
          };
        }

        if (cached.quotation == null) {
          cached.quotation = {
            enabled: false,
            quotationForm: null,
            status: CommonTaskStatus.Pending,
            version: 0,
          };
        }

        if (cached.samplingCheck == null) {
          cached.samplingCheck = {
            version: 0,
            type: DeliveryCheckSceneType.SamplingCheck,
            status: CommonTaskStatus.Pending,
            resultType: SamplingCheckResultType.None,
            items: [],
          };
        }

        // make sure the protocol version matches otherwise,
        if (cached.__VERSION === TaskManager.VERSION) {
          const detail = freeze(cached);
          cachedVersions = getDetailVersions(detail);
          debug(
            '[%s] loaded cached detail (version: %O)',
            this.taskNo,
            cachedVersions,
          );
          this._detail = detail;
          this.detail$.next(detail);
        } else {
          debug('[%s] cached detail discarded', this.taskNo);
          await deleteCachedFile(this.cacheFilePath);
        }
      } catch (e) {
        console.error(e);
      }
    }

    let shouldFetchFromServer = true;
    if (cachedVersions) {
      // check the versions
      try {
        debug('[%s] checking server versions... ', this.taskNo);
        const serverVersions = await this.taskService.getTaskDetailVersions(
          this.taskNo,
        );
        debug('[%s] loaded server versions: %O', this.taskNo, serverVersions);
        if (detailVersionsMatch(cachedVersions, serverVersions)) {
          shouldFetchFromServer = false;
          debug('[%s] versions match, use local cache', this.taskNo);
        }
      } catch (e) {
        console.error(e);
      }
    }

    if (shouldFetchFromServer) {
      // begin fetch from server.
      void this._fetch();
    } else {
      this.state$.next({ status: 'loaded' });
    }
  }

  private async _fetch(options?: {
    force?: boolean;
    refreshing?: boolean;
    projection?: TaskDetailProjection;
    callback?: (
      error: unknown | undefined,
      resp: VersionedResponse<TaskDetail> | undefined,
    ) => void;
  }) {
    try {
      const cachedVersions = this.cachedVersions;

      if (options?.refreshing) {
        this.state$.next({ status: 'loading', refreshing: true });
        debug('[%s] refreshing detail... ', this.taskNo);
      } else {
        if (this.state$.getValue().status !== 'loading') {
          this.state$.next({ status: 'loading' });
        }
        debug(
          '[%s] fetching detail with client versions: %O',
          this.taskNo,
          cachedVersions,
        );
      }

      // todo: how to prevent multiple fetch?
      const resp = await this.taskService.getTaskDetail(
        this.taskNo,
        options?.force
          ? undefined
          : {
              // projection will take precedence over versions
              projection: options?.projection,
              // this will only fetch updated contents
              projectionByVersions: options?.projection
                ? undefined
                : cachedVersions,
            },
      );

      const { versions, response } = resp;

      // update the detail object.
      this.produceWithVersions(versions, draft => {
        for (const key of kDetailProps) {
          if (response[key] != null || options?.force) {
            draft[key] = response[key] as any;
          }
        }
      });

      this.state$.next({ status: 'loaded' });

      options?.callback?.(undefined, resp);
    } catch (e) {
      console.error(e);
      this.state$.next({ status: 'error', error: e as Error });
      options?.callback?.(e, undefined);
    }
  }

  private _setupCacheAutoFlush() {
    // an internal subscription used to update the local cache.
    this._internalSubscription = this.detail$.subscribe(async detail => {
      if (detail.__TIMESTAMP === this._detail?.__TIMESTAMP) {
        return;
      }
      this._detail = detail;
      try {
        debug('[%s] saving detail to cache', this.taskNo);
        await writeContentsToCachedFile(
          this.cacheFilePath,
          __DEV__
            ? JSON.stringify(this._detail, null, 2)
            : JSON.stringify(this._detail),
        );
        debug('[%s] detail is kept updated to latest version', this.taskNo);
      } catch (e) {
        console.error(e);
      }
    });
  }

  //#endregion
}
